import { cn } from "@/lib/utils";
import { formatRelative, COLORS } from "@/lib/format";
import { ArrowUp, ArrowDown, Minus, ExternalLink } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

export function Overline({ children, className }) {
  return <div className={cn("overline", className)}>{children}</div>;
}

export function Panel({ className, children, ...props }) {
  return (
    <div className={cn("bg-card border border-border rounded-md", className)} {...props}>
      {children}
    </div>
  );
}

export function SectionTitle({ children, sub, right, className }) {
  return (
    <div className={cn("flex items-end justify-between gap-4 mb-4", className)}>
      <div>
        <h2 className="font-display text-2xl sm:text-3xl text-white">{children}</h2>
        {sub && <p className="text-sm text-muted-foreground mt-1">{sub}</p>}
      </div>
      {right}
    </div>
  );
}

export function SourceBadge({ source, updatedAt, testId }) {
  if (!source) return null;
  const rel = formatRelative(updatedAt);
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground border border-border"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--dot)]" style={{ "--dot": COLORS.positive }} />
      {source}{rel ? ` · ${rel}` : ""}
    </span>
  );
}

export function EmptyValue({ label = "Not verified", testId }) {
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center rounded border border-dashed border-border px-2 py-0.5 text-xs text-muted-foreground/70"
    >
      {label}
    </span>
  );
}

export function MetricField({ label, value, source, updatedAt, empty = "Not verified", href, testId }) {
  const has = value !== null && value !== undefined && value !== "";
  return (
    <div className="py-3 border-b border-border/60 last:border-0" data-testid={testId}>
      <Overline>{label}</Overline>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {has ? (
          href ? (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-white font-medium hover:text-[color:var(--acc)] transition-colors"
              style={{ "--acc": COLORS.positive }}
            >
              {value} <ExternalLink className="h-3.5 w-3.5" />
            </a>
          ) : (
            <span className="text-white font-semibold text-lg">{value}</span>
          )
        ) : (
          <EmptyValue label={empty} />
        )}
        {has && source && <SourceBadge source={source} updatedAt={updatedAt} />}
      </div>
    </div>
  );
}

export function DeltaValue({ delta, direction, suffix = "", className }) {
  if (delta === null || delta === undefined) {
    return <span className={cn("text-xs text-muted-foreground/70", className)}>No prior week</span>;
  }
  const map = {
    up: { Icon: ArrowUp, color: COLORS.positive },
    down: { Icon: ArrowDown, color: COLORS.negative },
    flat: { Icon: Minus, color: COLORS.muted },
  };
  const { Icon, color } = map[direction] || map.flat;
  return (
    <span className={cn("inline-flex items-center gap-1 text-sm font-semibold", className)} style={{ color }}>
      <Icon className="h-3.5 w-3.5" />
      {delta > 0 ? "+" : ""}{delta}{suffix}
    </span>
  );
}

export function SportBadge({ sport, className }) {
  const isBball = (sport || "").toLowerCase().includes("basket");
  return (
    <span
      className={cn(
        "inline-flex items-center rounded px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider border",
        isBball ? "border-[#3B82F6]/50 text-[#93c5fd]" : "border-[#E1FF00]/40 text-[#E1FF00]",
        className
      )}
    >
      {sport}
    </span>
  );
}

export function ClassBadge({ classification }) {
  if (!classification) return <EmptyValue label="Unverified" />;
  return (
    <span className="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider bg-secondary border border-border text-muted-foreground">
      {classification}
    </span>
  );
}

export function TrendChart({ data, dataKey, color = COLORS.positive, height = 180 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" vertical={false} />
        <XAxis dataKey="week" stroke="#525252" fontSize={11} tickLine={false} axisLine={false} />
        <YAxis stroke="#525252" fontSize={11} tickLine={false} axisLine={false} width={44} />
        <Tooltip
          contentStyle={{ background: "#0a0a0a", border: "1px solid #262626", borderRadius: 8, color: "#fff" }}
          labelStyle={{ color: "#a3a3a3" }}
        />
        <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={{ r: 3, fill: color }} activeDot={{ r: 5 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function EmptyState({ title, hint, testId, icon: Icon }) {
  return (
    <div
      data-testid={testId}
      className="flex flex-col items-center justify-center text-center rounded-md border border-dashed border-border py-10 px-6"
    >
      {Icon && <Icon className="h-6 w-6 text-muted-foreground/60 mb-3" />}
      <p className="text-sm font-medium text-muted-foreground">{title}</p>
      {hint && <p className="text-xs text-muted-foreground/70 mt-1 max-w-sm">{hint}</p>}
    </div>
  );
}
